import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import ProfilePictureAPI from '../services/profilePictureAPI';
import requestDeduplicator from '../services/requestDeduplicator';

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface ProfilePictureContextType {
  profileImage: string | undefined;
  refreshProfilePicture: () => Promise<void>;
  isLoading: boolean;
}

const ProfilePictureContext = createContext<ProfilePictureContextType | undefined>(undefined);

interface ProfilePictureProviderProps {
  children: ReactNode;
}

export const ProfilePictureProvider: React.FC<ProfilePictureProviderProps> = ({ children }) => {
  const [profileImage, setProfileImage] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const cacheRef = useRef<{ url: string | undefined; fetchedAt: number } | null>(null);
  const isFetchingRef = useRef<boolean>(false);

  const clearCache = useCallback(() => {
    cacheRef.current = null;
    setProfileImage(undefined);
  }, []);

  const fetchProfilePicture = useCallback(async (force: boolean = false) => {
    try {
      if (isFetchingRef.current) return;

      const userDetail = JSON.parse(localStorage.getItem('user') || "{}");
      const userId = userDetail?.user?.userId || userDetail?.user?._id;
      
      if (!userId) {
        clearCache();
        return;
      }

      // Check if we have a recent cached result (cache includes "no picture" result)
      const now = Date.now();
      if (!force && cacheRef.current && (now - cacheRef.current.fetchedAt) < CACHE_DURATION) {
        setProfileImage(cacheRef.current.url);
        return;
      }

      setIsLoading(true);
      isFetchingRef.current = true;
      
      // Use deduplication to prevent multiple simultaneous calls
      const response = await requestDeduplicator.execute(
        `profile-picture-${userId}`,
        () => ProfilePictureAPI.getProfilePictureInfo(userId)
      );
      
      const url = response.success && response.data?.publicUrl ? response.data.publicUrl : undefined;
      cacheRef.current = { url, fetchedAt: now };
      setProfileImage(url);
    } catch (error) {
      console.error('Error fetching profile picture:', error);
      cacheRef.current = { url: undefined, fetchedAt: Date.now() };
      setProfileImage(undefined);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [clearCache]);

  const refreshProfilePicture = async () => {
    await fetchProfilePicture(true);
  };

  // Fetch profile picture on mount
  useEffect(() => {
    fetchProfilePicture();
  }, [fetchProfilePicture]);

  // Listen for storage changes (when user logs in/out)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user') {
        if (e.newValue) {
          // User logged in, fetch profile picture
          fetchProfilePicture(true);
        } else {
          // User logged out, clear profile picture
          clearCache();
        }
      }
    };

    // Listen for custom profile picture update events
    const handleProfilePictureUpdate = () => {
      fetchProfilePicture(true);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('profilePictureUpdated', handleProfilePictureUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('profilePictureUpdated', handleProfilePictureUpdate);
    };
  }, [fetchProfilePicture, clearCache]);

  const value: ProfilePictureContextType = {
    profileImage,
    refreshProfilePicture,
    isLoading
  };

  return (
    <ProfilePictureContext.Provider value={value}>
      {children}
    </ProfilePictureContext.Provider>
  );
};

export const useProfilePicture = (): ProfilePictureContextType => {
  const context = useContext(ProfilePictureContext);
  if (context === undefined) {
    throw new Error('useProfilePicture must be used within a ProfilePictureProvider');
  }
  return context;
}; 